import {
	ToothDecayGrade,
	ToothDecayPulp,
	ToothDecayStage,
	ToothDiscolorationType,
	ToothFracturePosition,
	ToothFractureType,
	ToothPathology,
	ToothPresence,
	ToothToothWearZone,
} from '../../../../graphql/graphql';
import useTranslation from '../../../../Hooks/useTranslation';
import getHistoryValueSurfaces from './getHistoryValueSurfaces';

type SimplePathology = Omit<ToothPathology, '__typename' | 'createdAt'>;
export type SimplePathologyKey = keyof SimplePathology;
type SimplePathologyValue =
	| ToothPresence
	| ToothDecayStage
	| ToothDecayGrade
	| ToothDecayPulp
	| ToothFracturePosition
	| ToothFractureType
	| ToothDiscolorationType
	| ToothToothWearZone;

const SIMPLE_PATHOLOGY_KEY_MAP: Record<SimplePathologyKey, string> = {
	apical: 'patientTreatment.meddiDENT.dentalCross.pathologyModal.apical.title',
	decays: 'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.title',
	developmentDisorder:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.developmentDisorder.title',
	discoloration:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.discoloration.title',
	fractures:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.fracture.title',
	toothWear:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.title',
	rootDecay:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.rootDecay.title',
};

const SIMPLE_PATHOLOGY_VALUE_MAP: Record<SimplePathologyValue, string> = {
	[ToothPresence.Yes]: 'base.yes',
	[ToothPresence.No]: 'base.no',
	[ToothDecayStage.Dentin]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.stageDentin',
	[ToothDecayStage.Enamel]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.stageEnamel',
	[ToothDecayGrade.GradeA]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.gradeA',
	[ToothDecayGrade.GradeB]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.gradeB',
	[ToothDecayGrade.GradeC]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.gradeC',
	[ToothDecayPulp.Involved]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.pulpInvolved',
	[ToothDecayPulp.NotInvolved]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.pulpNotInvolved',
	[ToothFracturePosition.Horizontal]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.fracture.positionHorizontal',
	[ToothFracturePosition.Vertical]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.fracture.positionVertical',
	[ToothFractureType.Crown]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.fracture.typeCrown',
	[ToothFractureType.Root]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.fracture.typeRoot',
	[ToothDiscolorationType.Red]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.discoloration.red',
	[ToothDiscolorationType.Yellow]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.discoloration.yellow',
	[ToothDiscolorationType.Gray]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.discoloration.gray',
	[ToothToothWearZone.Buccal]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.zoneBuccal',
	[ToothToothWearZone.Lingual]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.zoneLingual',
	[ToothToothWearZone.Palatal]:
		'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.zonePalatal',
};

// TODO: types for pathology logs broke after changing fractures, decays and tooth wear
// fractures changed from one fracture to multiple, same for decays
// tooth wear separated into abrasion and erosion, so it lost surfaces and values at top level
// fix later
export default function parseSimplePathologyValue<T extends SimplePathologyKey>(
	key: T,
	value: SimplePathology[T],
) {
	const { t } = useTranslation();

	if (!value?.values) {
		return '';
	}

	let surfacesString = '';
	if (value.surfaces?.length) {
		surfacesString = ` - ${getHistoryValueSurfaces(value.surfaces)}`;
	}

	const valuesString = Object.entries(value.values)
		.map(([, subValue]) => {
			if (!subValue) return '';
			return t(SIMPLE_PATHOLOGY_VALUE_MAP[subValue]);
		})
		.join(', ');

	return `${t(
		SIMPLE_PATHOLOGY_KEY_MAP[key],
	)}${surfacesString} - ${valuesString}`;
}
